/* eslint-disable no-undef */
import axios from 'axios';
import ApiList from './api';
import getPkce from 'oauth-pkce';
import useCookie from './useCookie';
import { getPlainAuthKeyFromJwt } from '../utils/helpers';

const scope = 'vms.all';
const codeVerifierLocalStorageKey = 'plainVerifier';
const authServer = () => config.authServer;
const clientId = () => config.clientId;
const redirectUri = () => config.redirectUri;
const { setCookie, getCookie, removeCookie } = useCookie();

export async function rerouteToOAuthLogin() {
  // reroute to login panel that redirects back to the web app with an authorization code
  const { verifier, challenge } = await new Promise((resolve) => {
    getPkce(43, (error, { verifier, challenge }) => {
      if (error) throw error;
      resolve({ verifier, challenge });
    });
  });
  window.localStorage.setItem(codeVerifierLocalStorageKey, verifier);
  window.location.href = `${authServer()}/oauth2/authorize?client_id=${clientId()}&redirect_uri=${redirectUri()}&response_type=code&code_challenge=${challenge}&code_challenge_method=S256&scope=${scope}&loginUri=${redirectUri()}`;
}

export async function getToken(code) {
  // https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.3
  const params = new URLSearchParams();
  params.append('grant_type', 'authorization_code');
  params.append('client_id', clientId());
  params.append('code', code);
  params.append('code_verifier', localStorage.getItem(codeVerifierLocalStorageKey));
  params.append('redirect_uri', redirectUri());

  const axiosConfig = {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  };
  try {
    const response = await axios.post(`${authServer()}/oauth2/token`, params, axiosConfig);
    if (response.code === 302 && response.headers.location) {
      window.location.href = response.headers.location;
    } else {
      setApiBaseUrl(response.data.httpsBaseUrl.hostname);
      ApiList.token = response.data.access_token;
      setAuthCookie(response.data.access_token, response.data.expires_in + 's');
      localStorage.removeItem(codeVerifierLocalStorageKey);
    }
  } catch (error) {
    console.log(error);
  }
}

export async function getJwtFromExchangeToken(exchangeToken) {
  try {
    const response = await axios.get(`${authServer()}/api/v3.0/oneTimeCodes/${exchangeToken}`);
    ApiList.token = response.data.accessToken;
    const startDate = new Date().getTime();
    const endDate = new Date(response.data.expireTimestamp).getTime();
    const expiry = Math.floor((endDate - startDate) / 1000) + 's';
    setAuthCookie(response.data.accessToken, expiry);
    setApiBaseUrl(response.data.httpsBaseUrl.hostname);
    return response;
  } catch (error) {
    console.log(error);
  }
  return undefined;
}

export async function logOut() {
  const cachedAuthKey = authKey();
  try {
    const params = new URLSearchParams();
    params.append('token', cachedAuthKey);
    cachedAuthKey &&
      (await axios.post('/oauth2/revoke', params, {
        baseURL: authServer(),
        withCredentials: true,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }));
    const domain = ['localhost', '127.0.0.1'].includes(window.location.hostname)
      ? window.location.hostname
      : '.' + window.location.hostname.split('.').slice(-2).join('.');
    removeCookie('access_token', '/', domain);
    removeCookie('auth_key', '/', domain);
    removeCookie('root_url', '/', domain);
    await v1LogoutApiCall();
    rerouteToOAuthLogin();
  } catch (error) {
    console.log(error);
  }
}

/** Returns information about the token when active = true */
export async function introspectToken() {
  try {
    const encodedParams = new URLSearchParams();
    encodedParams.set('token', authKey());
    const response = await axios.post('/oauth2/introspect', encodedParams, {
      baseURL: authServer(),
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return { active: false };
  }
}

export function authKey() {
  return getCookie('access_token');
}

export function getBaseUrl() {
  return getCookie('root_url');
}

export function plainAuthKey() {
  return getCookie('auth_key');
}

function setAuthCookie(accessToken, expiry) {
  setCookie('access_token', accessToken, expiry, '/');
  // set the basic auth_key
  const plainAuthToken = getPlainAuthKeyFromJwt(accessToken);
  ApiList.plainAuthToken = plainAuthToken;
  setCookie('auth_key', plainAuthToken, expiry, '/');
}

function setApiBaseUrl(hostname) {
  const url = `https://${hostname}/api/v3.0`;
  ApiList.baseURL = url;
  setCookie('root_url', url, '0', '/');
}

/**
 * the function is called to clear the auth_key cookie written by v1 api (classic webapp).
 */
async function v1LogoutApiCall() {
  try {
    const v1ApiBaseURL = ApiList.baseURL.replace('https://api.', 'https://').replace('/api/v3.0', '');
    await axios.post(
      '/g/aaa/logout',
      {},
      {
        baseURL: v1ApiBaseURL,
        withCredentials: true,
      }
    );
  } catch (error) {
    console.error('Error during v1 logout API call:', error);
  }
}
